import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import {
  listLeagues,
  listOrganizations,
  listRestProfiles,
  listRestRules
} from "../graphql/queries";
import {
  createRestProfile,
  createRestRule,
  deleteRestProfile,
  deleteRestRule,
  updateRestProfile,
  updateRestRule
} from "../graphql/mutations";
import { formatApiError } from "../lib/formatApiError";
import AppDialog from "../components/AppDialog";
import { buildAdminScope } from "../lib/adminScope";

interface RulesPageProps {
  userRoles: string[];
}

interface Organization {
  id: string;
  name: string;
}

interface League {
  id: string;
  organizationId: string;
  admins?: string[] | null;
}

interface RestRule {
  id: string;
  name: string;
  ageGroup: string;
  global?: boolean | null;
  organizationId?: string | null;
  criteria?: string | null;
  weeklyOutsLimit?: number | null;
}

interface RestProfile {
  id: string;
  restRuleId: string;
  pitchCount: number;
  daysRest: number;
}

type RuleSortKey = "name" | "scope" | "criteria" | "outs";
type SortDirection = "asc" | "desc";

const RulesPage: React.FC<RulesPageProps> = ({ userRoles }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [rules, setRules] = useState<RestRule[]>([]);
  const [profiles, setProfiles] = useState<RestProfile[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [username, setUsername] = useState("");

  const [name, setName] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [scope, setScope] = useState<"GLOBAL" | "ORGANIZATION">("ORGANIZATION");
  const [organizationId, setOrganizationId] = useState("");
  const [criteria, setCriteria] = useState("BOTH");
  const [weeklyOutsLimit, setWeeklyOutsLimit] = useState("");
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [weeklyDialogRuleId, setWeeklyDialogRuleId] = useState<string | null>(null);
  const [weeklyDialogValue, setWeeklyDialogValue] = useState("");

  const [sortKey, setSortKey] = useState<RuleSortKey>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [filterText, setFilterText] = useState("");
  const [filterScope, setFilterScope] = useState<"all" | "global" | "organization">("all");
  const [filterCriteria, setFilterCriteria] = useState<"all" | "PITCHES" | "OUTS" | "BOTH">("all");
  const [filterOrganizationId, setFilterOrganizationId] = useState("");
  const [tierSortByRuleId, setTierSortByRuleId] = useState<Record<string, "pitchCount" | "daysRest">>({});
  const [tierSortDirectionByRuleId, setTierSortDirectionByRuleId] = useState<Record<string, SortDirection>>({});

  const [newPitchCount, setNewPitchCount] = useState<Record<string, string>>({});
  const [newDaysRest, setNewDaysRest] = useState<Record<string, string>>({});
  const [editingTierByRuleId, setEditingTierByRuleId] = useState<Record<string, string>>({});
  const [editingTierPitchById, setEditingTierPitchById] = useState<Record<string, string>>({});
  const [editingTierDaysById, setEditingTierDaysById] = useState<Record<string, string>>({});

  const adminScope = useMemo(
    () => buildAdminScope({ userRoles, username, organizations, leagues }),
    [leagues, organizations, userRoles, username]
  );
  const canManage = userRoles.includes("SuperAdmin") || userRoles.includes("Commish");
  const isSuperAdmin = adminScope.isGlobalAdmin && userRoles.includes("SuperAdmin");
  const pitchThresholdOptions = useMemo(() => {
    const values: number[] = [];
    for (let value = 0; value <= 180; value += 5) {
      values.push(value);
    }
    return values;
  }, []);
  const restDayOptions = useMemo(() => [0, 1, 2, 3, 4, 5, 6, 7], []);

  const managedOrganizationIds = adminScope.managedOrganizationIds;

  const canEditRule = (rule: RestRule): boolean => {
    if (!canManage) {
      return false;
    }
    if (isSuperAdmin) {
      return true;
    }
    if (rule.global) {
      return false;
    }
    if (!rule.organizationId) {
      return false;
    }
    return managedOrganizationIds.has(rule.organizationId);
  };

  const profilesByRule = useMemo(() => {
    const out: Record<string, RestProfile[]> = {};
    for (const profile of profiles) {
      const list = out[profile.restRuleId] ?? [];
      list.push(profile);
      out[profile.restRuleId] = list;
    }
    for (const key of Object.keys(out)) {
      const sortBy = tierSortByRuleId[key] ?? "pitchCount";
      const direction = tierSortDirectionByRuleId[key] ?? "asc";
      out[key].sort((a, b) => {
        const compare = sortBy === "daysRest" ? a.daysRest - b.daysRest : a.pitchCount - b.pitchCount;
        return direction === "asc" ? compare : -compare;
      });
    }
    return out;
  }, [profiles, tierSortByRuleId, tierSortDirectionByRuleId]);

  const orgById = useMemo(
    () => organizations.reduce<Record<string, string>>((acc, org) => ({ ...acc, [org.id]: org.name }), {}),
    [organizations]
  );

  const sortedRules = useMemo(() => {
    const visible = rules.filter((rule) => isSuperAdmin || rule.global || (rule.organizationId ? managedOrganizationIds.has(rule.organizationId) : false));
    const normalizedText = filterText.trim().toLowerCase();
    const filtered = visible.filter((rule) => {
      const scopeLabel = rule.global ? "global" : orgById[rule.organizationId ?? ""] ?? "";
      const normalizedCriteria = (rule.criteria ?? "BOTH").toUpperCase() as "PITCHES" | "OUTS" | "BOTH";
      const haystack = `${rule.name} ${rule.ageGroup ?? ""} ${scopeLabel} ${normalizedCriteria}`.toLowerCase();
      if (normalizedText && !haystack.includes(normalizedText)) {
        return false;
      }
      if (filterScope === "global" && !rule.global) {
        return false;
      }
      if (filterScope === "organization" && rule.global) {
        return false;
      }
      if (filterCriteria !== "all" && normalizedCriteria !== filterCriteria) {
        return false;
      }
      if (filterOrganizationId && rule.organizationId !== filterOrganizationId) {
        return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      let compare = 0;
      if (sortKey === "scope") {
        const aScope = a.global ? "GLOBAL" : orgById[a.organizationId ?? ""] ?? "ORG";
        const bScope = b.global ? "GLOBAL" : orgById[b.organizationId ?? ""] ?? "ORG";
        compare = aScope.localeCompare(bScope);
      } else if (sortKey === "criteria") {
        compare = (a.criteria ?? "BOTH").localeCompare(b.criteria ?? "BOTH");
      } else if (sortKey === "outs") {
        compare = (a.weeklyOutsLimit ?? -1) - (b.weeklyOutsLimit ?? -1);
      } else {
        compare = a.name.localeCompare(b.name);
      }
      return sortDirection === "asc" ? compare : -compare;
    });
  }, [filterCriteria, filterOrganizationId, filterScope, filterText, rules, isSuperAdmin, managedOrganizationIds, sortKey, sortDirection, orgById]);

  const visibleOrganizations = useMemo(
    () => (isSuperAdmin ? organizations : organizations.filter((org) => managedOrganizationIds.has(org.id))),
    [isSuperAdmin, organizations, managedOrganizationIds]
  );

  const loadData = async () => {
    try {
      setErrorMessage("");
      const authUser = await Auth.currentAuthenticatedUser();
      setUsername(authUser?.username ?? "");
      const [orgRes, leagueRes, ruleRes, profileRes] = await Promise.all([
        API.graphql(graphqlOperation(listOrganizations, { limit: 200 })),
        API.graphql(graphqlOperation(listLeagues, { limit: 400 })),
        API.graphql(graphqlOperation(listRestRules, { limit: 200 })),
        API.graphql(graphqlOperation(listRestProfiles, { limit: 500 }))
      ]);

      setOrganizations(((orgRes as any)?.data?.listOrganizations?.items ?? []).filter(Boolean));
      setLeagues(((leagueRes as any)?.data?.listLeagues?.items ?? []).filter(Boolean));
      setRules(((ruleRes as any)?.data?.listRestRules?.items ?? []).filter(Boolean));
      setProfiles(((profileRes as any)?.data?.listRestProfiles?.items ?? []).filter(Boolean));
    } catch (error) {
      setErrorMessage(`Failed to load rules: ${formatApiError(error)}`);
    }
  };

  const handleCreateRule = async () => {
    if (!canManage || !name.trim()) {
      return;
    }

    if (scope === "ORGANIZATION" && !organizationId) {
      setErrorMessage("Choose an organization for organization-scoped rules.");
      return;
    }
    if (!isSuperAdmin && scope === "GLOBAL") {
      setErrorMessage("Only SuperAdmin can create global rules.");
      return;
    }
    if (!isSuperAdmin && organizationId && !managedOrganizationIds.has(organizationId)) {
      setErrorMessage("Commish can only manage rules for their league organizations.");
      return;
    }

    try {
      setErrorMessage("");
      const input: Record<string, unknown> = {
        name: name.trim(),
        ageGroup: ageGroup.trim() || "All",
        global: scope === "GLOBAL",
        organizationId: scope === "ORGANIZATION" ? organizationId : null,
        criteria
      };

      if (weeklyOutsLimit.trim()) {
        const parsed = Number(weeklyOutsLimit);
        if (Number.isFinite(parsed) && parsed > 0) {
          input.weeklyOutsLimit = parsed;
        }
      }

      if (editingRuleId) {
        await API.graphql(graphqlOperation(updateRestRule, { input: { id: editingRuleId, ...input } }));
      } else {
        await API.graphql(graphqlOperation(createRestRule, { input }));
      }
      setName("");
      setAgeGroup("");
      setScope(isSuperAdmin ? "GLOBAL" : "ORGANIZATION");
      setOrganizationId("");
      setCriteria("BOTH");
      setWeeklyOutsLimit("");
      setEditingRuleId(null);
      setCreateOpen(false);
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to save rule: ${formatApiError(error)}`);
    }
  };

  const startEditRule = (rule: RestRule) => {
    setEditingRuleId(rule.id);
    setName(rule.name);
    setAgeGroup(rule.ageGroup ?? "");
    setScope(rule.global ? "GLOBAL" : "ORGANIZATION");
    setOrganizationId(rule.organizationId ?? "");
    setCriteria((rule.criteria ?? "BOTH").toUpperCase());
    setWeeklyOutsLimit(typeof rule.weeklyOutsLimit === "number" ? String(rule.weeklyOutsLimit) : "");
    setCreateOpen(true);
  };

  const handleDeleteRule = async (rule: RestRule) => {
    if (!canEditRule(rule) || !window.confirm("Delete this rule and all of its tiers?")) {
      return;
    }

    try {
      setErrorMessage("");
      const ruleProfiles = profilesByRule[rule.id] ?? [];
      await Promise.all(
        ruleProfiles.map((profile) =>
          API.graphql(graphqlOperation(deleteRestProfile, { input: { id: profile.id } }))
        )
      );
      await API.graphql(graphqlOperation(deleteRestRule, { input: { id: rule.id } }));
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to delete rule: ${formatApiError(error)}`);
    }
  };

  const handleAddTier = async (rule: RestRule) => {
    if (!canEditRule(rule)) {
      return;
    }

    const ruleId = rule.id;
    const pitchCount = Number(newPitchCount[ruleId]);
    const daysRest = Number(newDaysRest[ruleId]);
    if (!Number.isFinite(pitchCount) || pitchCount < 0 || !Number.isFinite(daysRest) || daysRest < 0) {
      setErrorMessage("Pitch count and days rest must be non-negative numbers.");
      return;
    }

    try {
      setErrorMessage("");
      await API.graphql(
        graphqlOperation(createRestProfile, {
          input: {
            restRuleId: ruleId,
            pitchCount,
            daysRest
          }
        })
      );
      setNewPitchCount((prev) => ({ ...prev, [ruleId]: "" }));
      setNewDaysRest((prev) => ({ ...prev, [ruleId]: "" }));
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to add tier: ${formatApiError(error)}`);
    }
  };

  const handleQuickCriteriaChange = async (rule: RestRule, nextCriteria: string) => {
    if (!canEditRule(rule)) {
      return;
    }
    try {
      setErrorMessage("");
      await API.graphql(
        graphqlOperation(updateRestRule, {
          input: { id: rule.id, criteria: nextCriteria }
        })
      );
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to update criteria: ${formatApiError(error)}`);
    }
  };

  const openWeeklyDialog = (rule: RestRule) => {
    setWeeklyDialogRuleId(rule.id);
    setWeeklyDialogValue(typeof rule.weeklyOutsLimit === "number" ? String(rule.weeklyOutsLimit) : "");
  };

  const saveWeeklyOutsDialog = async () => {
    if (!weeklyDialogRuleId) {
      return;
    }
    const rule = rules.find((item) => item.id === weeklyDialogRuleId);
    if (!rule || !canEditRule(rule)) {
      return;
    }
    try {
      setErrorMessage("");
      const parsed = Number(weeklyDialogValue);
      await API.graphql(
        graphqlOperation(updateRestRule, {
          input: {
            id: weeklyDialogRuleId,
            weeklyOutsLimit: Number.isFinite(parsed) && parsed > 0 ? parsed : null
          }
        })
      );
      setWeeklyDialogRuleId(null);
      setWeeklyDialogValue("");
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to update weekly outs: ${formatApiError(error)}`);
    }
  };

  const handleDeleteTier = async (id: string) => {
    if (!canManage) {
      return;
    }

    try {
      setErrorMessage("");
      await API.graphql(graphqlOperation(deleteRestProfile, { input: { id } }));
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to delete tier: ${formatApiError(error)}`);
    }
  };

  const startEditTier = (ruleId: string, profile: RestProfile) => {
    setEditingTierByRuleId((prev) => ({ ...prev, [ruleId]: profile.id }));
    setEditingTierPitchById((prev) => ({ ...prev, [profile.id]: String(profile.pitchCount) }));
    setEditingTierDaysById((prev) => ({ ...prev, [profile.id]: String(profile.daysRest) }));
  };

  const saveTier = async (rule: RestRule, profileId: string) => {
    if (!canEditRule(rule)) {
      return;
    }
    const pitchCount = Number(editingTierPitchById[profileId]);
    const daysRest = Number(editingTierDaysById[profileId]);
    if (!Number.isFinite(pitchCount) || pitchCount < 0 || !Number.isFinite(daysRest) || daysRest < 0) {
      setErrorMessage("Pitch count and days rest must be non-negative numbers.");
      return;
    }
    try {
      setErrorMessage("");
      await API.graphql(
        graphqlOperation(updateRestProfile, {
          input: {
            id: profileId,
            pitchCount,
            daysRest
          }
        })
      );
      setEditingTierByRuleId((prev) => ({ ...prev, [rule.id]: "" }));
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to save tier: ${formatApiError(error)}`);
    }
  };

  const toggleTierSort = (ruleId: string, key: "pitchCount" | "daysRest") => {
    const currentKey = tierSortByRuleId[ruleId] ?? "pitchCount";
    const currentDirection = tierSortDirectionByRuleId[ruleId] ?? "asc";
    if (currentKey === key) {
      setTierSortDirectionByRuleId((prev) => ({ ...prev, [ruleId]: currentDirection === "asc" ? "desc" : "asc" }));
      return;
    }
    setTierSortByRuleId((prev) => ({ ...prev, [ruleId]: key }));
    setTierSortDirectionByRuleId((prev) => ({ ...prev, [ruleId]: "asc" }));
  };

  const toggleRuleSort = (key: RuleSortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection("asc");
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-2">Pitch Count Rules</h1>
      <p className="text-gray-600 mb-6">
        Configure global or organization rules, then assign them to organizations and leagues.
      </p>
      {errorMessage && <p className="mb-4" style={{ color: "#c1121f" }}>{errorMessage}</p>}

      {canManage && (
        <div className="bubble-card p-4 mb-4 flex gap-2">
          <button
            onClick={() => {
              setEditingRuleId(null);
              setName("");
              setAgeGroup("");
              setScope(isSuperAdmin ? "GLOBAL" : "ORGANIZATION");
              setOrganizationId("");
              setCriteria("BOTH");
              setWeeklyOutsLimit("");
              setCreateOpen(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Add Rule
          </button>
        </div>
      )}

      <div className="bubble-card p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-2">
        <input
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          placeholder="Filter rule name, age, scope"
          className="border px-3 py-2 rounded"
        />
        <select value={filterScope} onChange={(event) => setFilterScope(event.target.value as "all" | "global" | "organization")} className="border px-3 py-2 rounded">
          <option value="all">All scopes</option>
          <option value="global">Global</option>
          <option value="organization">Organization</option>
        </select>
        <select value={filterCriteria} onChange={(event) => setFilterCriteria(event.target.value as "all" | "PITCHES" | "OUTS" | "BOTH")} className="border px-3 py-2 rounded">
          <option value="all">All criteria</option>
          <option value="PITCHES">Pitches</option>
          <option value="OUTS">Outs</option>
          <option value="BOTH">Both</option>
        </select>
        <select value={filterOrganizationId} onChange={(event) => setFilterOrganizationId(event.target.value)} className="border px-3 py-2 rounded">
          <option value="">All organizations</option>
          {visibleOrganizations.map((organization) => (
            <option key={organization.id} value={organization.id}>
              {organization.name}
            </option>
          ))}
        </select>
      </div>

      <div className="bubble-table-wrapper">
        <table className="bubble-table">
          <thead>
            <tr>
              <th><button className="table-sort" onClick={() => toggleRuleSort("name")}>Rule</button></th>
              <th><button className="table-sort" onClick={() => toggleRuleSort("scope")}>Scope</button></th>
              <th><button className="table-sort" onClick={() => toggleRuleSort("criteria")}>Criteria</button></th>
              <th><button className="table-sort" onClick={() => toggleRuleSort("outs")}>Weekly Outs</button></th>
              <th>Pitch Tiers</th>
              {canManage && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sortedRules.map((rule) => (
              <tr key={rule.id}>
                <td>
                  <div className="font-semibold">{rule.name}</div>
                  <div className="text-sm text-gray-600">{rule.ageGroup || "All"}</div>
                </td>
                <td>{rule.global ? "Global" : `Org: ${orgById[rule.organizationId ?? ""] ?? "Unknown"}`}</td>
                <td>
                  <select
                    value={(rule.criteria ?? "BOTH").toUpperCase()}
                    onChange={(event) => handleQuickCriteriaChange(rule, event.target.value)}
                    disabled={!canEditRule(rule)}
                    className="border px-2 py-1 rounded"
                  >
                    <option value="PITCHES">Pitches</option>
                    <option value="OUTS">Outs</option>
                    <option value="BOTH">Both</option>
                  </select>
                </td>
                <td>
                  <div>{typeof rule.weeklyOutsLimit === "number" ? rule.weeklyOutsLimit : "-"}</div>
                  {canEditRule(rule) && (
                    <button className="table-sort text-sm" onClick={() => openWeeklyDialog(rule)}>
                      Edit Weekly Outs
                    </button>
                  )}
                </td>
                <td>
                  <div className="bubble-pill-wrap mb-2">
                    {(profilesByRule[rule.id] ?? []).map((profile) => (
                      <span key={profile.id} className="bubble-pill">
                        {profile.pitchCount}+ {"->"} {profile.daysRest}d
                      </span>
                    ))}
                  </div>
                  {canEditRule(rule) && (
                    <div className="flex gap-2 items-center">
                      <select
                        value={newPitchCount[rule.id] ?? ""}
                        onChange={(event) => setNewPitchCount((prev) => ({ ...prev, [rule.id]: event.target.value }))}
                        className="border px-2 py-1 rounded"
                      >
                        <option value="">Pitch</option>
                        {pitchThresholdOptions.map((option) => (
                          <option key={`${rule.id}-p-${option}`} value={option}>{option}</option>
                        ))}
                      </select>
                      <select
                        value={newDaysRest[rule.id] ?? ""}
                        onChange={(event) => setNewDaysRest((prev) => ({ ...prev, [rule.id]: event.target.value }))}
                        className="border px-2 py-1 rounded"
                      >
                        <option value="">Days</option>
                        {restDayOptions.map((option) => (
                          <option key={`${rule.id}-d-${option}`} value={option}>{option}</option>
                        ))}
                      </select>
                      <button onClick={() => handleAddTier(rule)} className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700">
                        Add
                      </button>
                    </div>
                  )}
                </td>
                {canManage && (
                  <td>
                    {canEditRule(rule) && (
                      <div className="flex gap-2">
                        <button onClick={() => startEditRule(rule)} className="bg-yellow-500 text-white px-2 py-1 rounded hover:bg-yellow-600">
                          Edit
                        </button>
                        <button onClick={() => handleDeleteRule(rule)} className="bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700">
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AppDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={editingRuleId ? "Edit Rule" : "Add Rule"}
        footer={
          <>
            <button onClick={() => setCreateOpen(false)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={handleCreateRule} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Rule name"
            className="border px-3 py-2 rounded"
          />
          <input
            value={ageGroup}
            onChange={(event) => setAgeGroup(event.target.value)}
            placeholder="Age group (e.g., 10U)"
            className="border px-3 py-2 rounded"
          />
          <select value={scope} onChange={(event) => setScope(event.target.value as "GLOBAL" | "ORGANIZATION")} className="border px-3 py-2 rounded">
            {isSuperAdmin && <option value="GLOBAL">Global</option>}
            <option value="ORGANIZATION">Organization-specific</option>
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
          <select
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
            className="border px-3 py-2 rounded"
            disabled={scope !== "ORGANIZATION"}
          >
            <option value="">Select organization</option>
            {visibleOrganizations.map((organization) => (
              <option key={organization.id} value={organization.id}>
                {organization.name}
              </option>
            ))}
          </select>
          <select value={criteria} onChange={(event) => setCriteria(event.target.value)} className="border px-3 py-2 rounded">
            <option value="PITCHES">Pitches only</option>
            <option value="OUTS">Outs only</option>
            <option value="BOTH">Pitches + outs</option>
          </select>
          <input
            type="number"
            min={0}
            value={weeklyOutsLimit}
            onChange={(event) => setWeeklyOutsLimit(event.target.value)}
            placeholder="Weekly outs limit (optional)"
            className="border px-3 py-2 rounded"
          />
        </div>
      </AppDialog>

      <AppDialog
        open={Boolean(weeklyDialogRuleId)}
        onClose={() => setWeeklyDialogRuleId(null)}
        title="Edit Weekly Outs"
        footer={
          <>
            <button onClick={() => setWeeklyDialogRuleId(null)} className="bg-yellow-500 text-white px-3 py-2 rounded">Cancel</button>
            <button onClick={saveWeeklyOutsDialog} className="bg-blue-600 text-white px-3 py-2 rounded">Save</button>
          </>
        }
      >
        <input
          type="number"
          min={0}
          value={weeklyDialogValue}
          onChange={(event) => setWeeklyDialogValue(event.target.value)}
          placeholder="Leave blank or 0 to clear"
          className="border px-3 py-2 rounded w-full"
        />
      </AppDialog>
    </div>
  );
};

export default RulesPage;
